import type {
  BriefCheckRequest,
  BriefFinding,
  BriefMember,
  CheckTriggerKind,
  ProjectBrief,
} from "./types";
import { client, raise } from "./repository/client";
import { citationsOf, issueProposalOf } from "./repository/findingParsers";
export { errorHint } from "./repository/client";
export * from "./repository/versions";
export * from "./repository/appendices";
export { citationsOf, issueProposalOf } from "./repository/findingParsers";

/** Typed brief reads and writes. Findings always use the visibility-filtered view. */
const IN_CHUNK_SIZE = 200;

// ---------------------------------------------------------------- brief ----

export async function fetchBrief(projectId: number): Promise<ProjectBrief | null> {
  const { data, error } = await client()
    .from("project_briefs")
    .select("id, building_project_id, org_id, building_id, folder_id, created_at, created_by")
    .eq("building_project_id", projectId)
    .maybeSingle();
  if (error) raise(error);
  return (data as ProjectBrief | null) ?? null;
}

/** Create-or-get; NULL for a viewer with no brief yet (they may not create one). */
export async function ensureBrief(projectId: number): Promise<ProjectBrief | null> {
  const { data, error } = await client().rpc("get_or_create_project_brief", {
    p_project_id: projectId,
  });
  if (error) raise(error);
  return (data as ProjectBrief | null) ?? null;
}

// ------------------------------------------------------------ documents ----

const MEMBER_COLUMNS =
  "brief_id, lineage_key, version_group, document_id, filename, version, content_hash, state, " +
  "folder_id, folder_path, project_id, content_type, size, effective_modified_at, document_status";

/** The brief's live set: every lineage head in the Brief folder's subtree, under the caller's RLS. */
export async function fetchBriefMembers(briefId: number): Promise<BriefMember[]> {
  const { data, error } = await client()
    .from("project_brief_documents_current")
    .select(MEMBER_COLUMNS)
    .eq("brief_id", briefId)
    .order("filename", { ascending: true });
  if (error) raise(error);
  return ((data ?? []) as unknown as BriefMember[]).map((m) => ({
    ...m,
    folder_path: m.folder_path ?? [],
  }));
}

/** How many of the brief's documents the caller's folder access hides from them. */
export async function fetchHiddenDocumentCount(briefId: number): Promise<number> {
  const { data, error } = await client().rpc("brief_hidden_document_count", {
    p_brief_id: briefId,
  });
  if (error) raise(error);
  return typeof data === "number" ? data : 0;
}

interface FindingRow {
  id: number;
  brief_id: number;
  project_id: number;
  building_id: number;
  kind: BriefFinding["kind"];
  severity: BriefFinding["severity"];
  title: string;
  body: string;
  citations: unknown;
  related_document_ids: number[] | null;
  status: BriefFinding["status"];
  resolved_at: string | null;
  created_at: string;
  /** Absent when read with the pre-suggested-issue column list. */
  issue_proposal?: unknown;
  filed_issue_id?: number | null;
}

const LEGACY_FINDING_COLUMNS =
  "id, brief_id, project_id, building_id, kind, severity, title, body, citations, " +
  "related_document_ids, status, resolved_at, created_at";
const FINDING_COLUMNS = `${LEGACY_FINDING_COLUMNS}, issue_proposal, filed_issue_id`;

/** Postgres "undefined column": the suggested-issue migration has not applied yet. */
const UNDEFINED_COLUMN = "42703";

/**
 * Franklin's suggestions. Open ones are what the page and the nav dot both
 * count, so they are read in full (the cap is a safety net, far above any real brief); resolved
 * ones accumulate over months and are folded away, so only the newest few
 * hundred come back.
 */
export const OPEN_FINDINGS_LIMIT = 1000;
export const RESOLVED_FINDINGS_LIMIT = 300;

async function fetchFindingRows(briefId: number, open: boolean): Promise<FindingRow[]> {
  const query = (columns: string) => {
    const base = client()
      .from("project_brief_findings_visible")
      .select(columns)
      .eq("brief_id", briefId);
    const scoped = open ? base.eq("status", "open") : base.neq("status", "open");
    return scoped
      .order("created_at", { ascending: false })
      .limit(open ? OPEN_FINDINGS_LIMIT : RESOLVED_FINDINGS_LIMIT);
  };
  let { data, error } = await query(FINDING_COLUMNS);
  // Before the suggested-issue migration applies, read suggestions without it.
  if (error?.code === UNDEFINED_COLUMN) ({ data, error } = await query(LEGACY_FINDING_COLUMNS));
  if (error) raise(error);
  return (data ?? []) as unknown as FindingRow[];
}

export async function fetchBriefFindings(briefId: number): Promise<BriefFinding[]> {
  const [openRows, resolvedRows] = await Promise.all([
    fetchFindingRows(briefId, true),
    fetchFindingRows(briefId, false),
  ]);
  return [...openRows, ...resolvedRows]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((row) => ({
      ...row,
      citations: citationsOf(row.citations),
      related_document_ids: row.related_document_ids ?? [],
      issue_proposal: issueProposalOf(row.issue_proposal),
      filed_issue_id: row.filed_issue_id ?? null,
    }));
}

/** What an issue was raised from, for the issue panel's back-link. */
export interface BriefIssueOrigin {
  kind: "appendix" | "suggestion";
  id: number;
  title: string;
  building_id: number;
  project_id: number;
}

type OriginRow = Omit<BriefIssueOrigin, "kind">;

/**
 * The appendix or suggestion an issue was raised from. An appendix wins: an
 * issue is linked to at most one of each, and the appendix is the brief's own
 * text. Before the suggested-issue migration applies, only appendices count.
 */
export async function fetchBriefOriginForIssue(issueId: number): Promise<BriefIssueOrigin | null> {
  const { data, error } = await client()
    .from("project_brief_appendices")
    .select("id, title, building_id, project_id")
    .eq("filed_issue_id", issueId)
    .limit(1)
    .maybeSingle();
  if (error) raise(error);
  if (data) return { kind: "appendix", ...(data as OriginRow) };

  const suggestion = await client()
    .from("project_brief_findings_visible")
    .select("id, title, building_id, project_id")
    .eq("filed_issue_id", issueId)
    .limit(1)
    .maybeSingle();
  if (suggestion.error?.code === UNDEFINED_COLUMN) return null;
  if (suggestion.error) raise(suggestion.error);
  return suggestion.data ? { kind: "suggestion", ...(suggestion.data as OriginRow) } : null;
}

interface LinkedFindingIssue {
  finding_id: number;
  issue_id: number;
  issue_number: number;
}

/**
 * Links the issue the PM form just created to the suggestion that proposed
 * it. Once only; the suggestion stays open (raising an issue does not settle
 * it), and the issue must be a live issue of the brief's project.
 */
export async function linkFindingIssue(
  findingId: number,
  issueId: number
): Promise<LinkedFindingIssue> {
  const { data, error } = await client().rpc("brief_link_finding_issue", {
    p_finding_id: findingId,
    p_issue_id: issueId,
  });
  if (error) raise(error);
  return data as LinkedFindingIssue;
}

/**
 * Accepts one of Franklin's suggestions by appending it as an appendix: the
 * text as the person edited it, authored by Franklin and confirmed by them.
 * Only documents the suggestion cites can be attached.
 */
export async function acceptSuggestion(
  findingId: number,
  body: string,
  documentIds: number[]
): Promise<void> {
  const { error } = await client().rpc("brief_accept_suggestion", {
    p_finding_id: findingId,
    p_body: body,
    p_document_ids: documentIds,
  });
  if (error) raise(error);
}

/** Franklin's open suggestions on a project — the sidebar dot. */
export async function fetchOpenFindingsCount(projectId: number): Promise<number> {
  const { count, error } = await client()
    .from("project_brief_findings")
    .select("id", { count: "exact", head: true })
    .eq("project_id", projectId)
    .eq("status", "open");
  if (error) raise(error);
  return count ?? 0;
}

/** A suggestion is dismissed, and a dismissed one reopened; accepting appends an appendix. */
export type ResolveAction = "dismiss" | "reopen";

export async function resolveBriefFinding(
  findingId: number,
  action: ResolveAction,
  reason?: string
): Promise<void> {
  const { error } = await client().rpc("brief_resolve_finding", {
    p_finding_id: findingId,
    p_action: action,
    p_reason: reason ?? null,
  });
  if (error) raise(error);
}

// --------------------------------------------------------------- checks ----

interface CheckRow {
  id: number;
  brief_id: number;
  status: BriefCheckRequest["status"];
  trigger_kind: CheckTriggerKind;
  trigger_payload: { kinds?: unknown } | null;
  completed_at: string | null;
  error: string | null;
  created_at: string;
}

const CHECK_COLUMNS =
  "id, brief_id, status, trigger_kind, trigger_payload, completed_at, error, created_at";

const TRIGGER_KINDS: CheckTriggerKind[] = [
  "document_processed",
  "brief_changed",
  "issue_activity",
  "building_data",
  "manual",
];

function isTriggerKind(value: unknown): value is CheckTriggerKind {
  return typeof value === "string" && (TRIGGER_KINDS as string[]).includes(value);
}

export function toCheckRequest(row: CheckRow): BriefCheckRequest {
  const kindsRaw = row.trigger_payload?.kinds;
  const kinds = Array.isArray(kindsRaw) ? kindsRaw.filter(isTriggerKind) : [];
  return {
    id: row.id,
    brief_id: row.brief_id,
    status: row.status,
    trigger_kind: row.trigger_kind,
    kinds: kinds.length > 0 ? kinds : [row.trigger_kind],
    completed_at: row.completed_at,
    error: row.error,
    created_at: row.created_at,
  };
}

/** The newest few runs — enough for "running now" and "last run". */
export const CHECKS_LIMIT = 5;

export async function fetchBriefChecks(briefId: number): Promise<BriefCheckRequest[]> {
  const { data, error } = await client()
    .from("project_brief_check_requests")
    .select(CHECK_COLUMNS)
    .eq("brief_id", briefId)
    .order("created_at", { ascending: false })
    .limit(CHECKS_LIMIT);
  if (error) raise(error);
  return ((data ?? []) as unknown as CheckRow[]).map(toCheckRequest);
}

export async function requestBriefCheck(briefId: number): Promise<number> {
  const { data, error } = await client().rpc("brief_request_check", { p_brief_id: briefId });
  if (error) raise(error);
  return data as number;
}

export async function cancelBriefCheck(requestId: number): Promise<void> {
  const { error } = await client().rpc("brief_cancel_check", { p_request_id: requestId });
  if (error) raise(error);
}

// -------------------------------------------------------------- lookups ----

/** Issue numbers for the ids suggestions cite; invisible issues are simply absent. */
export async function fetchIssueNumbers(issueIds: number[]): Promise<Map<number, number>> {
  const numbers = new Map<number, number>();
  if (issueIds.length === 0) return numbers;
  const { data, error } = await client()
    .from("pm_issues")
    .select("id, issue_number")
    .in("id", issueIds);
  if (error) raise(error);
  for (const row of (data ?? []) as Array<{ id: number; issue_number: number }>) {
    numbers.set(row.id, row.issue_number);
  }
  return numbers;
}
